import React, { useContext, useState } from 'react'
import { Link } from 'react-router-dom'
import { sessionContext, useGetBusinessIDIfExists } from '@marketing-milk/frontend'
import { getNavLinks } from './getNavLinks'
import { RailLink } from './RailLink'
import { RailClientSelector } from './RailClientSelector'
import { RailUserMenu } from './RailUserMenu'
import { RAIL_COLLAPSED_WIDTH, RAIL_EXPANDED_WIDTH } from './railMetrics'

interface Props {
  pinned: boolean
  togglePinned: () => void
  /** Desktop only. Touch has no hover, and the mobile rail opens a drawer instead. */
  hoverToExpand?: boolean
}

const LOGO =
  'https://www.marketingmilk.com/wp-content/themes/marketing-milk/static/img/marketing-milk-logo-white.svg'

export const Rail = ({ pinned, togglePinned, hoverToExpand }: Props) => {
  const { user } = useContext(sessionContext)
  const businessID = useGetBusinessIDIfExists()
  const links = getNavLinks(businessID, user).filter(l => l.visible !== false)

  const [hovered, setHovered] = useState(false)
  // The account menu portals to the body over a full-screen backdrop, so the
  // pointer leaves the rail the instant it opens. Without this the rail
  // collapses out from under the menu it just spawned.
  const [menuOpen, setMenuOpen] = useState(false)

  const peeking = Boolean(hoverToExpand) && (hovered || menuOpen)
  const expanded = pinned || peeking

  return (
    <nav
      style={{ width: expanded ? RAIL_EXPANDED_WIDTH : RAIL_COLLAPSED_WIDTH }}
      onMouseEnter={hoverToExpand ? () => setHovered(true) : undefined}
      onMouseLeave={hoverToExpand ? () => setHovered(false) : undefined}
      className="fixed left-0 top-0 z-50 flex h-screen flex-col bg-mm-secondary-950 px-4 py-4 transition-all duration-300 ease-in-out"
      aria-label="Main navigation"
      data-testid="app-rail"
    >
      <div className="flex h-10 flex-shrink-0 items-center">
        <Link to="/dashboard" className="flex w-10 flex-shrink-0 justify-center" title="Businesses">
          <img src={LOGO} alt="Marketing Milk" style={{ maxHeight: 24 }} />
        </Link>
      </div>

      {/* Outside the scrolling nav on purpose: on a short screen the selector
          must not be something you have to scroll the rail to reach. */}
      <div className="mt-4 flex-shrink-0 border-t border-mm-secondary-900 pt-4">
        <div className="flex h-10 items-stretch">
          <RailClientSelector expanded={expanded} onRequestExpand={togglePinned} />
        </div>
      </div>

      <div className="rail-scroll mt-4 flex-1 space-y-1 overflow-y-auto overflow-x-hidden">
        <RailLink label="Businesses" icon="briefcase" to="dashboard" expanded={expanded} />
        {links.map((route, i) => (
          <React.Fragment key={route.label}>
            {(i === 0 || links[i - 1].group !== route.group) && (
              <div className="mx-2 border-t border-mm-secondary-900 pt-1" aria-hidden />
            )}
            <RailLink
              label={route.label}
              icon={route.icon}
              to={route.externalLink ?? route.url}
              external={Boolean(route.externalLink)}
              expanded={expanded}
              // Business-scoped destinations stay visible without one so the rail
              // shows what picking a business unlocks, rather than an empty list.
              disabled={!businessID && !route.externalLink}
            />
          </React.Fragment>
        ))}
      </div>

      {/* The toggle sits with the account rather than above the sections: at the
          top it reads as an eleventh destination in the collapsed icon column. */}
      <div className="mt-4 flex-shrink-0 space-y-1 border-t border-mm-secondary-900 pt-4">
        <button
          type="button"
          onClick={togglePinned}
          aria-label={pinned ? 'Collapse navigation' : 'Expand navigation'}
          aria-pressed={pinned}
          title={pinned ? 'Collapse navigation' : 'Keep navigation open'}
          className="group flex h-10 w-full items-center rounded-lg transition-colors"
        >
          <i
            className={`fas fa-chevron-double-${
              pinned ? 'left' : 'right'
            } w-10 flex-shrink-0 text-center text-xl leading-10 text-mm-neutral-1-50 group-hover:text-mm-primary-400`}
            aria-hidden
          />
          <span
            className={`overflow-hidden whitespace-nowrap text-sm font-medium text-mm-primary-200 transition-all duration-300 group-hover:text-white ${
              expanded ? 'ml-1 max-w-full opacity-100' : 'max-w-0 opacity-0'
            }`}
          >
            {pinned ? 'Collapse' : 'Keep open'}
          </span>
        </button>
        <RailUserMenu expanded={expanded} onOpenChange={setMenuOpen} />
      </div>
    </nav>
  )
}
